import { z } from 'zod';

export const zApiError = z.object({
  code: z.string(),
  message: z.string(),
});

const zResponseStatus = z.enum(['Failure', 'Success']);

export const zApiResponse = z.object({
  status: zResponseStatus,
  errors: zApiError.array(),
  data: z.unknown(),
});

export const zApiOrder = z.object({
  numberOfShippedOrderLines: z.number(),
  customerNumber: z.string(),
  orderDate: z.string(),
  orderNumber: z.string(),
  orderNumberAliases: z.string().array().nullable(),
  orderLines: z
    .object({
      uid: z.string(),
      descriptionTranslationKey: z.string().nullable(),
      labelTranslationKey: z.string().nullable(),
      lineNumber: z.string(),
      mayBeReturned: z.boolean(),
      mayBeClaimed: z.boolean(),
      salesOrderLineId: z.number(),
      quantity: z.number(),
      shippedQuantity: z.number().nullable().optional(),
      product: z.object({
        description: z.string(),
        imageUrl: z.string(),
        productNumber: z.string(),
        variantDescription: z.string().nullable(),
        variant: z.string().nullable(),
      }),
    })
    .array(),
});

export const zDigitalOrderResponse = z.object({
  orders: zApiOrder.array(),
  customerOrderNumbers: z.string().array().nullable(),
});

export const zSettingsFont = z.object({
  family: z.string(),
  url: z.string().optional().nullable(),
});

export const zCountry = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
});

export const zSettingsResponse = z.object({
  interactionColor: z.string(),
  cornerRadius: z.string(),
  fontFamily: z.string().optional(),
  fonts: zSettingsFont.array(),
  productSearchEnabled: z.boolean(),
  redirectUrl: z.string().optional(),
  showB2BCSVImport: z.boolean(),
  shippingFormEnabled: z.boolean(),
  countries: zCountry.array().nullable(),
  digitalReturnSettingsId: z.number().optional(),
});

export const zTranslationResponse = z.object({
  iso6381: z.string(),
  translations: z
    .object({
      key: z.string(),
      value: z.string(),
    })
    .array(),
});

export const zCarriersResponse = z.object({
  carrierServices: z
    .object({
      carrier: z.string(),
      logoUrl: z.string(),
      service: z.string(),
      code: z.string(),
    })
    .array(),
});

export const zSummaryReturnOrderline = z.object({
  salesOrderLineId: z.number(),
  returnedQuantity: z.number(),
  refundValue: z.number(),
  comment: z.string(),
  reasonCodes: z.object({
    id: z.number(),
    rrmId: z.number(),
    code: z.string(),
  }),
  imageReferences: z.string().array(),
  exchange: z.object({
    productNumber: z.string(),
    variant: z.string(),
  }),
  regretPurchase: z.boolean(),
  decision: z.object({
    handledByCustomerSupport: z.boolean(),
    sendBackItem: z.boolean(),
  }),
});

export const zSummaryExtraOrderLines = z.object({
  type: z.string(),
  productNumber: z.string(),
  lineNumber: z.string(),
  description: z.string(),
  price: z.number(),
  reason: z.string(),
});

export const zSummaryResponse = z.object({
  returnOrders: z
    .object({
      salesOrderNumber: z.string(),
      returnOrderNumber: z.string(),
      returnSource: z.string(),
      returnOrderDate: z.string(),
      returnFulfilmentType: z.string(),
      returnOrderLines: zSummaryReturnOrderline.array(),
      extraReturnOrderLines: zSummaryExtraOrderLines.array(),
    })
    .array(),
  chosenCarrierService: z.string(),
  summaryResult: z.object({
    adjustedForShipment: z.boolean(),
    policyApplied: z.boolean(),
  }),
  returnInstructions: z
    .object({
      step: z.string(),
      instructionKey: z.string(),
    })
    .array(),
});

export const zReturnInstruction = z.object({
  step: z.number(),
  instruction: z.string(),
});

const zReturnError = z.object({
  code: z.string(),
  message: z.string(),
});

const returnStatusEnum = z.enum(['PartialSuccess', 'Success', 'Failure']);

export const zReturnResponse = z.object({
  errors: z.array(zReturnError),
  data: z.object({
    returnLabelUrl: z.string(),
    returnInstructions: zReturnInstruction.array(),
  }),
  status: returnStatusEnum,
});
